// File: packages/components/src/common/top-referral/TopReferralBar.tsx
import React, { useEffect, useState } from 'react';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, Tooltip } from '@dopomogai/ui';
import { X, Gift, Copy, Check } from 'lucide-react';

type Accent =
  | 'amber' | 'emerald' | 'blue' | 'violet' | 'cyan' | 'rose' | 'slate' | 'gray';

const accentBg = (a: Accent) =>
  a === 'emerald' ? 'bg-emerald-100' :
  a === 'blue'    ? 'bg-blue-100' :
  a === 'violet'  ? 'bg-violet-100' :
  a === 'cyan'    ? 'bg-cyan-100' :
  a === 'rose'    ? 'bg-rose-100' :
  a === 'slate'   ? 'bg-slate-100' :
  a === 'gray'    ? 'bg-gray-100' : 'bg-amber-100';

export type TopReferralBarProps = {
  text?: string;
  textKey?: string;
  cta?: { label?: string; labelKey?: string; actionId: string };
  onAction?: (actionId: string) => void;

  // New visuals/behavior
  accent?: Accent;              // default 'amber'
  sticky?: boolean;             // position: sticky top-0
  showIcon?: boolean;           // gift icon at left
  showCopy?: boolean;           // copy referral link button
  copyText?: string;            // text/URL to copy
  copyTooltip?: string;         // tooltip content
  showClose?: boolean;
  storageKey?: string;          // persists dismissal
  className?: string;
};

export const TopReferralBar: React.FC<TopReferralBarProps> = ({
  text,
  textKey,
  cta,
  onAction,
  accent = 'amber',
  sticky = true,
  showIcon = true,
  showCopy = false,
  copyText,
  copyTooltip = 'Copy your referral link',
  showClose = true,
  storageKey = 'referralBarDismissed',
  className,
}) => {
  const { t } = useTranslation('homepage');
  const resolvedText = textKey ? t(textKey) : (text ?? 'Refer a client, earn 12% in credits');
  const [dismissed, setDismissed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    if (!storageKey) return;
    try {
      const val = typeof window !== 'undefined' ? window.localStorage.getItem(storageKey) : null;
      if (val === '1') setDismissed(true);
    } catch {}
  }, [storageKey]);

  const handleClose = () => {
    setDismissed(true);
    try {
      if (storageKey && typeof window !== 'undefined') {
        window.localStorage.setItem(storageKey, '1');
      }
    } catch {}
  };

  const handleCopy = async () => {
    if (!copyText || typeof navigator === 'undefined') return;
    try {
      await navigator.clipboard.writeText(copyText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  if (dismissed) return null;

  return (
    <div
      className={clsx(
        'w-full border-b-2 border-black',
        accentBg(accent),
        sticky && 'sticky top-0 z-50',
        // subtle slide-in on mount
        mounted ? 'translate-y-0 opacity-100' : '-translate-y-2 opacity-0',
        'transition-all duration-200',
        className
      )}
      role="region"
      aria-label="Referral incentive"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-2.5 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-sm md:text-base text-slate-900 font-semibold">
          {showIcon && <Gift size={16} className="text-slate-800" aria-hidden="true" />}
          <span>{resolvedText}</span>
        </div>

        <div className="flex items-center gap-2">
          {showCopy && copyText ? (
            <Tooltip text={copied ? 'Copied!' : copyTooltip}>
              <IconButton
                icon={copied ? <Check size={16} /> : <Copy size={16} />}
                ariaLabel="Copy referral link"
                variant="secondary"
                size="sm"
                onClick={handleCopy}
              />
            </Tooltip>
          ) : null}

          {cta ? (
            <Button
              size="sm"
              variant="secondary"
              text={cta.labelKey ? t(cta.labelKey) : (cta.label ?? 'Get your link')}
              onClick={() => onAction?.(cta.actionId)}
            />
          ) : null}

          {showClose ? (
            <IconButton
              icon={<X size={16} />}
              ariaLabel="Dismiss referral bar"
              variant="ghost"
              size="sm"
              onClick={handleClose}
            />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default TopReferralBar;
